import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Modal,
  Stack,
  Group,
  Box,
  Text,
  Badge,
  Button,
  UnstyledButton,
  Loader,
  Center,
  Tabs,
  TextInput,
  Paper,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { FaSearch } from "react-icons/fa";
import AudioPlayer from "./audio_player";
import {
  GET_WORKSPACE_VOICES,
  SEARCH_ELEVENLABS_VOICES,
  ADD_VOICE_TO_WORKSPACE,
} from "../services/companies";
import { Voice, VoicesResponse } from "../types/voice";
import { useAuth } from "../contexts/auth.context";

export interface VoiceSelectorModalProps {
  opened: boolean;
  onClose: () => void;
  onSelect: (voice: Voice) => void;
  currentVoiceId?: string;
}

export default function VoiceSelectorModal({
  opened,
  onClose,
  onSelect,
  currentVoiceId,
}: VoiceSelectorModalProps) {
  const { userData } = useAuth();

  const [allVoices, setAllVoices] = useState<Voice[]>([]);
  const [filteredVoices, setFilteredVoices] = useState<Voice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState<string | null>("voices");

  // Add voice tab state
  const [addSearchQuery, setAddSearchQuery] = useState("");
  const [addSearchResults, setAddSearchResults] = useState<Voice[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addingVoiceId, setAddingVoiceId] = useState<string | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const loadVoices = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: VoicesResponse = await GET_WORKSPACE_VOICES();
      const combined = [
        ...(response.default_voices || []),
        ...(response.workspace_voices || []),
      ];
      setAllVoices(combined);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to load voices",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Filter voices on search query change
  useEffect(() => {
    if (!searchQuery) {
      setFilteredVoices(allVoices);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFilteredVoices(allVoices.filter((v) => v.name.toLowerCase().includes(q) || v.provider.toLowerCase().includes(q)));
  }, [searchQuery, allVoices]);

  // Load voices when modal opens, reset state
  useEffect(() => {
    if (opened) {
      loadVoices();
      setAddSearchQuery("");
      setAddSearchResults([]);
      setSelectedTab("voices");
      setSearchQuery("");
    }
  }, [opened, loadVoices]);

  // Debounced ElevenLabs search
  const handleAddSearchChange = (query: string) => {
    setAddSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query.trim()) {
      setAddSearchResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const results = await SEARCH_ELEVENLABS_VOICES(query);
        setAddSearchResults(Array.isArray(results) ? results : []);
      } catch {
        notifications.show({ title: "Error", message: "Failed to search voices", color: "red" });
      } finally {
        setIsSearching(false);
      }
    }, 500);
  };

  const handleAddVoice = async (voice: Voice) => {
    try {
      setAddingVoiceId(voice.elevenlabs_voice_id || voice.id);
      await ADD_VOICE_TO_WORKSPACE({
        elevenlabs_voice_id: voice.elevenlabs_voice_id || voice.id,
        public_user_id: voice.public_user_id,
        voice_name: voice.name,
      });
      notifications.show({ title: "Voice added", message: `"${voice.name}" added to your workspace`, color: "green" });
      // Mark as added in results and refresh the library
      setAddSearchResults((prev) =>
        prev.map((v) =>
          (v.elevenlabs_voice_id || v.id) === (voice.elevenlabs_voice_id || voice.id)
            ? { ...v, already_added_to_workspace: true }
            : v
        )
      );
      await loadVoices();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add voice",
        color: "red",
      });
    } finally {
      setAddingVoiceId(null);
    }
  };

  const handleSelectVoice = (voice: Voice) => {
    onSelect(voice);
    onClose();
  };

  const renderVoiceRow = (voice: Voice, selectable = true) => (
    <Group key={voice.id} align="center" wrap="nowrap" gap="sm">
      <UnstyledButton
        onClick={selectable ? () => handleSelectVoice(voice) : undefined}
        style={{
          padding: "12px",
          borderRadius: "8px",
          border:
            selectable && currentVoiceId === voice.retell_voice_id
              ? "2px solid var(--mantine-color-blue-6)"
              : "1px solid var(--mantine-color-gray-3)",
          backgroundColor:
            selectable && currentVoiceId === voice.retell_voice_id
              ? "var(--mantine-color-blue-0)"
              : "transparent",
          cursor: selectable ? "pointer" : "default",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={500} size="sm">{voice.name}</Text>
            <Group gap="xs" mt={2} wrap="wrap">
              <Badge size="xs" variant="outline">{voice.provider}</Badge>
              {voice.is_default && <Badge size="xs" color="blue">Default</Badge>}
              {voice.is_workspace_voice && <Badge size="xs" color="grape">Workspace</Badge>}
            </Group>
          </Box>
          {selectable && currentVoiceId === voice.retell_voice_id && (
            <Badge color="blue" size="sm">Selected</Badge>
          )}
        </Group>
      </UnstyledButton>
      {voice.preview_url && (
        <Box style={{ flexShrink: 0 }}>
          <AudioPlayer url={voice.preview_url} />
        </Box>
      )}
    </Group>
  );

  return (
    <Modal opened={opened} onClose={onClose} title="Select Voice" size="lg">
      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Tabs value={selectedTab} onChange={setSelectedTab} defaultValue="voices">
          <Tabs.List mb="md">
            <Tabs.Tab value="voices">
              Voices
              {allVoices.length > 0 && (
                <Badge size="xs" ml="xs">{allVoices.length}</Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="add">Add Voice</Tabs.Tab>
          </Tabs.List>

          {selectedTab === "voices" && (
            <TextInput
              placeholder="Filter voices..."
              leftSection={<FaSearch size={14} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              mb="sm"
            />
          )}

          <Tabs.Panel value="voices">
            <Stack gap="xs">
              {filteredVoices.length > 0 ? (
                filteredVoices.map((v) => renderVoiceRow(v))
              ) : (
                <Paper p="lg" withBorder ta="center">
                  <Text c="dimmed" size="sm">
                    {allVoices.length === 0 ? "No voices available. Use the \"Add Voice\" tab." : "No voices match your search"}
                  </Text>
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="add">
            <Stack gap="sm">
              <TextInput
                placeholder="Search ElevenLabs voices..."
                leftSection={isSearching ? <Loader size={14} /> : <FaSearch size={14} />}
                value={addSearchQuery}
                onChange={(e) => handleAddSearchChange(e.currentTarget.value)}
              />
              {addSearchResults.length > 0 ? (
                <Stack gap="xs">
                  {addSearchResults.map((voice) => (
                    <Paper key={voice.id} p="sm" withBorder radius="md">
                      <Stack gap="xs">
                        <Group justify="space-between" align="flex-start">
                          <Box style={{ flex: 1 }}>
                            <Text fw={500} size="sm">{voice.name}</Text>
                            <Group gap="xs" mt={2}>
                              <Badge size="xs" variant="outline">{voice.provider}</Badge>
                              {voice.already_added_to_workspace && (
                                <Badge size="xs" color="green">Added</Badge>
                              )}
                            </Group>
                          </Box>
                          <Button
                            size="xs"
                            variant={voice.already_added_to_workspace ? "default" : "filled"}
                            disabled={voice.already_added_to_workspace}
                            loading={addingVoiceId === (voice.elevenlabs_voice_id || voice.id)}
                            onClick={() => handleAddVoice(voice)}
                          >
                            {voice.already_added_to_workspace ? "Added" : "Add"}
                          </Button>
                        </Group>
                        {voice.preview_url && <AudioPlayer url={voice.preview_url} />}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : addSearchQuery && !isSearching ? (
                <Paper p="lg" withBorder ta="center">
                  <Text c="dimmed" size="sm">No voices found for "{addSearchQuery}"</Text>
                </Paper>
              ) : !addSearchQuery ? (
                <Paper p="lg" withBorder ta="center">
                  <Text c="dimmed" size="sm">Type to search for ElevenLabs voices to add to your workspace</Text>
                </Paper>
              ) : null}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      )}
    </Modal>
  );
}


